/**
 * Balance overrides (Phase 7): a document of numbers laid over the shared JSON data. Every
 * field carries the same bound as the JSON schema it patches, so a published version can never
 * hand the simulation a nonsense value; structure (weapon kinds, ability lists, tiers, the
 * roster) is never overridable. `applyOverrides` in `index.ts` merges a document into the
 * catalogs and re-parses the result with the full schemas.
 */
import { z } from 'zod';
const finite = z.number().finite();
const positive = finite.positive();
const nonNegative = finite.nonnegative();
const fraction = finite.min(0).max(1);
const int = z.number().int();
const partial = (shape) => z.object(shape).partial().strict();
/** The global rules (`balance.json`) minus everything structural. */
export const BalanceOverrideSchema = partial({
    shieldRegenDelaySeconds: positive,
    overheatLockSeconds: positive,
    ventDissipationScale: positive,
    aimHeightFraction: fraction,
    hitLocation: partial({
        legBand: fraction,
        armLateralFraction: fraction,
        rearArcDeg: positive,
        rearCritMultiplier: positive,
    }),
    armor: partial({ max: fraction }),
    legDamage: partial({ maxSlow: fraction, atHpFraction: positive }),
    armDamage: partial({ maxSpread: nonNegative, atHpFraction: positive }),
    ultimate: partial({
        perDamageDealt: nonNegative,
        perDamageTaken: nonNegative,
        perSecond: nonNegative,
    }),
    suddenDeath: partial({ drainPerSecond: positive }),
    scoring: partial({
        roundWin: nonNegative,
        matchWin: nonNegative,
        accuracyBonusMax: nonNegative,
        accuracyLow: fraction,
        accuracyHigh: fraction,
        timeBonusPerRound: nonNegative,
        firstBlood: nonNegative,
        xpDivisor: positive,
        credits: partial({
            base: nonNegative,
            perRoundWin: nonNegative,
            perMatchWin: nonNegative,
            modeScale: partial({
                practice: nonNegative,
                botFill: nonNegative,
                casual: nonNegative,
                ranked: nonNegative,
            }),
        }),
    }),
    economy: partial({
        minDamageForPayout: nonNegative,
        dailyCreditCap: int.nonnegative(),
        repeatOpponent: partial({
            windowHours: positive,
            freeMeetings: int.nonnegative(),
            gainScale: fraction,
        }),
    }),
    progression: partial({ levelCap: int.min(1), xpBase: positive, xpExponent: positive }),
    rating: partial({
        initial: positive,
        initialRd: positive,
        initialVolatility: positive,
        tau: positive,
        minRd: positive,
        maxRd: positive,
        placementMatches: int.nonnegative(),
        placementMaxRd: positive,
    }),
});
/** A mech's numbers; weapons, abilities, slots and the chassis stay as the JSON says. */
export const MechOverrideSchema = partial({
    weightTons: positive,
    hp: positive,
    armor: partial({
        legs: finite.min(0).max(0.8),
        torso: finite.min(0).max(0.8),
        arms: finite.min(0).max(0.8),
    }),
    shield: nonNegative,
    shieldRegen: nonNegative,
    heatCap: positive,
    heatDissipation: positive,
    speed: positive,
    boostSpeed: positive,
    boostCost: nonNegative,
    boostEngageEnergy: nonNegative,
    turnRate: positive,
    acceleration: positive,
    deceleration: positive,
    airControl: fraction,
    energy: positive,
    energyRegen: nonNegative,
    jumpJets: partial({
        launchVelocity: positive,
        energyCost: nonNegative,
        thrust: nonNegative,
        thrustEnergyPerSecond: nonNegative,
    }),
    unlockCost: int.nonnegative(),
});
/** A weapon's numbers; its kind and blocks stay as the JSON says (a block absent in the JSON is refused on merge). */
export const WeaponOverrideSchema = partial({
    damage: positive,
    fireRate: positive,
    heat: nonNegative,
    targetHeat: nonNegative,
    range: partial({
        optimal: positive,
        falloffStart: nonNegative,
        max: positive,
        minDamageFactor: fraction,
    }),
    magazine: partial({ size: int.positive(), reloadSeconds: positive }),
    burst: partial({ count: int.min(2), intervalSeconds: positive }),
    spinUpSeconds: nonNegative,
    recoil: nonNegative,
    hitscan: partial({
        pellets: int.positive(),
        spreadDeg: nonNegative,
        bloomPerShot: fraction,
        bloomDecayPerSecond: nonNegative,
        maxBloomDeg: nonNegative,
    }),
    projectile: partial({
        speed: positive,
        gravity: nonNegative,
        radius: positive,
        lifeSeconds: positive,
        splash: partial({ radius: positive, damage: positive, minFactor: fraction }),
    }),
    beam: partial({
        width: positive,
        maxSeconds: positive.nullable(),
        coolSeconds: nonNegative,
        warmupSeconds: nonNegative,
    }),
    melee: partial({
        reach: positive,
        arcDeg: positive,
        windupSeconds: nonNegative,
        activeSeconds: positive,
        recoverySeconds: nonNegative,
        knockback: nonNegative,
        knockUp: nonNegative,
    }),
});
const id = z.string().regex(/^[a-z][a-z0-9-]*$/);
/** One balance version's content. Ids are checked against the catalogs by `applyOverrides`. */
export const BalanceOverridesSchema = z
    .object({
    balance: BalanceOverrideSchema.optional(),
    mechs: z.record(id, MechOverrideSchema).optional(),
    weapons: z.record(id, WeaponOverrideSchema).optional(),
    prices: z
        .object({
        modules: z.record(id, int.positive()).optional(),
        cosmetics: z.record(id, int.positive()).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/** The published document as the API serves it and the game server carries it into matches. */
export const ActiveBalanceSchema = z
    .object({
    /** 0 = the built-in data, no row. */
    version: int.nonnegative(),
    overrides: BalanceOverridesSchema,
    publishedAt: z.iso.datetime().nullable(),
})
    .strict();
export const EMPTY_OVERRIDES = {};
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Lays `patch` over `base`: nested plain objects merge, everything else (numbers, arrays,
 * null) replaces. `reference` is the fully parsed value (defaults applied, derived fields
 * present) that decides which keys exist, so an override can change any number the schema
 * knows, never add structure; `base` is the raw JSON the schema will re-parse. A subtree the
 * JSON leaves to defaults starts from the reference's value.
 */
export function deepMerge(base, patch, path = '', reference = base) {
    if (!isPlainObject(patch))
        return (patch === undefined ? base : patch);
    if (!isPlainObject(reference)) {
        throw new Error(`override at "${path || '(root)'}" patches a value that is not an object`);
    }
    const from = isPlainObject(base) ? base : reference;
    const out = { ...from };
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            continue;
        const at = path ? `${path}.${key}` : key;
        if (!(key in reference))
            throw new Error(`override at "${at}" names a field the data lacks`);
        out[key] = isPlainObject(value)
            ? deepMerge(from[key] ?? reference[key], value, at, reference[key])
            : value;
    }
    return out;
}
/** Every numeric leaf of a document as `path → value`, for the editor and the diff view. */
export function numericLeaves(value, prefix = '') {
    if (typeof value === 'number')
        return [[prefix, value]];
    if (!isPlainObject(value))
        return [];
    const out = [];
    for (const [key, child] of Object.entries(value)) {
        out.push(...numericLeaves(child, prefix ? `${prefix}.${key}` : key));
    }
    return out;
}
/** The inverse of a flat `path → value` map: a nested patch document. */
export function nestLeaves(leaves) {
    const out = {};
    for (const [path, value] of Object.entries(leaves)) {
        const parts = path.split('.');
        let node = out;
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i] ?? '';
            const next = node[part];
            if (isPlainObject(next))
                node = next;
            else {
                const fresh = {};
                node[part] = fresh;
                node = fresh;
            }
        }
        node[parts[parts.length - 1] ?? ''] = value;
    }
    return out;
}
//# sourceMappingURL=overrides-schema.js.map